import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useGym } from '../contexts/GymContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Settings,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Wallet,
  PiggyBank,
  AlertTriangle,
  CheckCircle2,
  Calculator,
  Save,
  RefreshCw,
  Lock,
  Calendar,
  CreditCard,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { formatCurrency } from '../utils/formatters';
import { DrillDownModal } from '../components/DrillDownModal';
import { Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';

// Cores padrão do sistema - Visual moderno verde
const PRIMARY_COLOR = '#22C55E';
const SECONDARY_COLOR = '#10B981';
const DANGER_COLOR = '#EF4444';
const WARNING_COLOR = '#F59E0B';
const SUCCESS_COLOR = '#22C55E';

export const FluxoCaixaPage = () => {
  const { selectedGym } = useGym();
  const [loading, setLoading] = useState(true);
  const [fluxo, setFluxo] = useState(null);
  const [drillDown, setDrillDown] = useState({ open: false, tipo: null, mes: null, ano: null });
  const [configOpen, setConfigOpen] = useState(false);
  const [fecharMesOpen, setFecharMesOpen] = useState(false);
  const [carteiraOpen, setCarteiraOpen] = useState(false);
  const [saldoEditando, setSaldoEditando] = useState(null);
  const [mesSelecionado, setMesSelecionado] = useState(null);
  
  // Filtros
  const [filtroAno, setFiltroAno] = useState(new Date().getFullYear());
  const [filtroPeriodo, setFiltroPeriodo] = useState('12'); // 6, 12, 24 meses
  
  const [configCenarios, setConfigCenarios] = useState({
    pessimista_receita: -10,
    pessimista_despesa: 15,
    realista_receita: 0,
    realista_despesa: 0,
    otimista_receita: 5,
    otimista_despesa: -5,
    retirada_media_mensal: 0
  });

  const [fechamentoForm, setFechamentoForm] = useState({
    saldo_inicial: '',
    saldo_final: '',
    observacao: ''
  });

  const [carteiraForm, setCarteiraForm] = useState({
    valor: '',
    data_consulta: new Date().toISOString().split('T')[0],
    fonte: '',
    observacao: ''
  });

  const loadData = useCallback(async () => {
    if (!selectedGym) return;
    
    try {
      setLoading(true);
      const [fluxoRes, configRes] = await Promise.all([
        api.get(`/fluxo-caixa-completo?gym_id=${selectedGym.id}&meses=${parseInt(filtroPeriodo) + 12}`),
        api.get(`/configuracao-cenarios?gym_id=${selectedGym.id}`)
      ]);
      
      setFluxo(fluxoRes.data);
      setConfigCenarios(configRes.data);
    } catch (error) {
      toast.error('Erro ao carregar fluxo de caixa');
    } finally {
      setLoading(false);
    }
  }, [selectedGym, filtroPeriodo]);

  useEffect(() => {
    if (selectedGym) {
      loadData();
    }
  }, [selectedGym, loadData]);

  // Filtrar dados do fluxo
  const fluxoFiltrado = fluxo?.fluxo_mensal?.filter(m => {
    if (filtroAno && filtroAno !== 'todos') {
      return m.ano === parseInt(filtroAno);
    }
    return true;
  }).slice(0, parseInt(filtroPeriodo)) || [];

  // Anos disponíveis para filtro
  const anosDisponiveis = [...new Set(fluxo?.fluxo_mensal?.map(m => m.ano) || [])].sort((a, b) => b - a);

  const openDrill = (tipo, mes, ano) => setDrillDown({ open: true, tipo, mes, ano });

  const handleSaveConfig = async () => {
    try {
      await api.put(`/configuracao-cenarios?gym_id=${selectedGym.id}`, configCenarios);
      toast.success('Configuração salva!');
      setConfigOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar configuração');
    }
  };

  const handleSaveSaldoReal = async (mes, ano, saldoFinal) => {
    try {
      await api.post('/saldo-real-banco', {
        gym_id: selectedGym.id,
        mes: mes,
        ano: ano,
        saldo_final: parseFloat(saldoFinal)
      });
      toast.success('Saldo real salvo!');
      setSaldoEditando(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar saldo');
    }
  };

  const handleFecharMes = async () => {
    if (!mesSelecionado) return;
    
    const saldoInicial = parseFloat(fechamentoForm.saldo_inicial) || 0;
    const saldoFinal = parseFloat(fechamentoForm.saldo_final) || 0;
    
    // Buscar o mês no fluxo
    const mesData = fluxo?.fluxo_mensal.find(m => m.mes === mesSelecionado.mes && m.ano === mesSelecionado.ano);
    if (!mesData) {
      toast.error('Mês não encontrado');
      return;
    }
    
    // Calcular resíduo
    const entradasCalculadas = mesData.entradas_caixa + mesData.outras_entradas_aportes;
    const saidasCalculadas = mesData.saidas_operacionais + mesData.investimentos_realizados + mesData.parcelas_financiamento + mesData.retirada_socio;
    const saldoFinalCalculado = saldoInicial + entradasCalculadas - saidasCalculadas;
    const residuo = saldoFinal - saldoFinalCalculado;
    
    if (Math.abs(residuo) > 0.01) {
      toast.error(`Resíduo de ${formatCurrency(residuo)} - O mês não pode ser fechado. Verifique os lançamentos.`);
      return;
    }
    
    try {
      await api.post('/saldo-real-banco', {
        gym_id: selectedGym.id,
        mes: mesSelecionado.mes,
        ano: mesSelecionado.ano,
        saldo_inicial: saldoInicial,
        saldo_final: saldoFinal,
        observacao: fechamentoForm.observacao || 'Mês fechado'
      });
      
      toast.success('Mês fechado com sucesso! Resíduo = ZERO ✓');
      setFecharMesOpen(false);
      setMesSelecionado(null);
      setFechamentoForm({ saldo_inicial: '', saldo_final: '', observacao: '' });
      loadData();
    } catch (error) {
      toast.error('Erro ao fechar mês');
    }
  };

  const handleSaveCarteira = async () => {
    if (!carteiraForm.valor) {
      toast.error('Informe o valor da carteira');
      return;
    }
    
    try {
      await api.post('/carteira-receber-real', {
        gym_id: selectedGym.id,
        valor: parseFloat(carteiraForm.valor),
        data_consulta: carteiraForm.data_consulta,
        fonte: carteiraForm.fonte,
        observacao: carteiraForm.observacao
      });
      
      toast.success('Carteira a receber salva!');
      setCarteiraOpen(false);
      setCarteiraForm({ valor: '', data_consulta: new Date().toISOString().split('T')[0], fonte: '', observacao: '' });
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar carteira');
    }
  };

  const getCelulaClass = (valor, tipo = 'normal') => {
    if (valor === null || valor === undefined) return 'text-zinc-500';
    
    if (tipo === 'positivo') {
      return valor >= 0 ? 'text-green-400' : 'text-red-400';
    }
    if (tipo === 'gap') {
      return valor > 0 ? 'text-yellow-400' : 'text-green-400';
    }
    if (tipo === 'cenario') {
      return valor >= 0 ? 'text-blue-400' : 'text-red-400';
    }
    return 'text-white';
  };

  // Dados reais do ano (Entradas x Saídas x Saldo acumulado) para o gráfico
  const dadosGraficoReal = (fluxo?.fluxo_mensal || []).map(m => ({
    mes: `${m.mes?.substring(0, 3)}/${m.ano?.toString().substring(2)}`,
    entradas: m.entradas_caixa,
    saidas: (m.saidas_operacionais || 0) + (m.investimentos_realizados || 0) + (m.parcelas_financiamento || 0),
    saldo: m.caixa_final_calculado
  }));

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: PRIMARY_COLOR }}></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 overflow-x-hidden" data-testid="fluxo-caixa-page">
        {/* Header */}
        <div className="space-y-4">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">Fluxo de Caixa</h1>
            <p className="text-zinc-400 mt-1 text-sm sm:text-base">Visão detalhada estilo planilha CFO</p>
          </div>
          
          {/* Filtros e Ações - tudo junto em mobile */}
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={filtroAno}
              onChange={(e) => setFiltroAno(e.target.value)}
              className="h-10 px-3 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-emerald-500 focus:outline-none"
            >
              <option value="todos">Todos</option>
              {anosDisponiveis.map(ano => (
                <option key={ano} value={ano}>{ano}</option>
              ))}
            </select>
            
            <select
              value={filtroPeriodo}
              onChange={(e) => setFiltroPeriodo(e.target.value)}
              className="h-10 px-3 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-emerald-500 focus:outline-none"
            >
              <option value="6">6 meses</option>
              <option value="12">12 meses</option>
              <option value="24">24 meses</option>
            </select>
            
            <Button variant="outline" onClick={loadData} className="border-zinc-700 h-10 px-3">
              <RefreshCw className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Atualizar</span>
            </Button>
            
            {/* Carteira a Receber */}
            <Dialog open={carteiraOpen} onOpenChange={setCarteiraOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 h-10 px-3">
                  <CreditCard className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Carteira Real</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800">
                <DialogHeader>
                  <DialogTitle className="text-white flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-cyan-400" />
                    Carteira a Receber Real
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4">
                  <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                    <p className="text-sm text-cyan-300">
                      Informe o valor que as adquirentes mostram como &quot;a receber&quot;. 
                      Isso é uma <strong>foto do momento</strong> — o valor muda todo dia conforme os recebimentos entram.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-300">Valor a Receber (R$)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={carteiraForm.valor}
                        onChange={(e) => setCarteiraForm({...carteiraForm, valor: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        placeholder="0,00"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-300">Data da Consulta</Label>
                      <Input
                        type="date"
                        value={carteiraForm.data_consulta}
                        onChange={(e) => setCarteiraForm({...carteiraForm, data_consulta: e.target.value})}
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-zinc-300">Fonte (opcional)</Label>
                    <Input
                      value={carteiraForm.fonte}
                      onChange={(e) => setCarteiraForm({...carteiraForm, fonte: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white"
                      placeholder="Ex: Stone, PagSeguro, Cielo"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-zinc-300">Observação</Label>
                    <Input
                      value={carteiraForm.observacao}
                      onChange={(e) => setCarteiraForm({...carteiraForm, observacao: e.target.value})}
                      className="bg-zinc-800 border-zinc-700 text-white"
                      placeholder="Ex: Consultado no app às 14h"
                    />
                  </div>
                  
                  <Button onClick={handleSaveCarteira} className="w-full" style={{ backgroundColor: SECONDARY_COLOR }}>
                    <Save className="w-4 h-4 mr-2" />
                    Salvar Posição
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Configuração de Cenários */}
            <Dialog open={configOpen} onOpenChange={setConfigOpen}>
              <DialogTrigger asChild>
                <Button style={{ backgroundColor: SECONDARY_COLOR }} className="h-10 px-3">
                  <Settings className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Cenários</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-white">Configuração de Cenários</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4">
                  {/* Cenário Pessimista */}
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                    <h3 className="text-red-400 font-medium mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Pessimista
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-zinc-400 text-xs">Receita (%)</Label>
                        <Input
                          type="number"
                          value={configCenarios.pessimista_receita}
                          onChange={(e) => setConfigCenarios({...configCenarios, pessimista_receita: parseFloat(e.target.value) || 0})}
                          className="bg-zinc-800 border-zinc-700 text-white h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Despesa (%)</Label>
                        <Input
                          type="number"
                          value={configCenarios.pessimista_despesa}
                          onChange={(e) => setConfigCenarios({...configCenarios, pessimista_despesa: parseFloat(e.target.value) || 0})}
                          className="bg-zinc-800 border-zinc-700 text-white h-9"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Cenário Realista */}
                  <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                    <h3 className="text-yellow-400 font-medium mb-2 flex items-center gap-2">
                      <Calculator className="w-4 h-4" />
                      Realista
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-zinc-400 text-xs">Receita (%)</Label>
                        <Input
                          type="number"
                          value={configCenarios.realista_receita}
                          onChange={(e) => setConfigCenarios({...configCenarios, realista_receita: parseFloat(e.target.value) || 0})}
                          className="bg-zinc-800 border-zinc-700 text-white h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Despesa (%)</Label>
                        <Input
                          type="number"
                          value={configCenarios.realista_despesa}
                          onChange={(e) => setConfigCenarios({...configCenarios, realista_despesa: parseFloat(e.target.value) || 0})}
                          className="bg-zinc-800 border-zinc-700 text-white h-9"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Cenário Otimista */}
                  <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                    <h3 className="text-green-400 font-medium mb-2 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Otimista
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-zinc-400 text-xs">Receita (%)</Label>
                        <Input
                          type="number"
                          value={configCenarios.otimista_receita}
                          onChange={(e) => setConfigCenarios({...configCenarios, otimista_receita: parseFloat(e.target.value) || 0})}
                          className="bg-zinc-800 border-zinc-700 text-white h-9"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Despesa (%)</Label>
                        <Input
                          type="number"
                          value={configCenarios.otimista_despesa}
                          onChange={(e) => setConfigCenarios({...configCenarios, otimista_despesa: parseFloat(e.target.value) || 0})}
                          className="bg-zinc-800 border-zinc-700 text-white h-9"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Retirada Média */}
                  <div className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                    <h3 className="text-zinc-300 font-medium mb-2 flex items-center gap-2">
                      <Wallet className="w-4 h-4" />
                      Retirada Média do Sócio
                    </h3>
                    <Input
                      type="number"
                      step="0.01"
                      value={configCenarios.retirada_media_mensal}
                      onChange={(e) => setConfigCenarios({...configCenarios, retirada_media_mensal: parseFloat(e.target.value) || 0})}
                      className="bg-zinc-800 border-zinc-700 text-white"
                      placeholder="0,00"
                    />
                  </div>
                  
                  <Button onClick={handleSaveConfig} className="w-full" style={{ backgroundColor: PRIMARY_COLOR }}>
                    <Save className="w-4 h-4 mr-2" />
                    Salvar
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Resumo */}
        {fluxo && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 rounded-lg shrink-0" style={{ backgroundColor: `${PRIMARY_COLOR}20` }}>
                    <TrendingUp className="w-5 h-5" style={{ color: PRIMARY_COLOR }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-zinc-500">Média Entradas (6m)</p>
                    <p className="text-base sm:text-lg font-bold font-numbers leading-tight break-words" style={{ color: PRIMARY_COLOR }}>
                      {formatCurrency(fluxo.resumo?.media_entradas_6m || 0)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 rounded-lg shrink-0" style={{ backgroundColor: `${DANGER_COLOR}20` }}>
                    <TrendingDown className="w-5 h-5" style={{ color: DANGER_COLOR }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-zinc-500">Média Saídas OP (6m)</p>
                    <p className="text-base sm:text-lg font-bold font-numbers leading-tight break-words" style={{ color: DANGER_COLOR }}>
                      {formatCurrency(fluxo.resumo?.media_despesas_op_6m || 0)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 rounded-lg shrink-0" style={{ backgroundColor: `${SECONDARY_COLOR}20` }}>
                    <PiggyBank className="w-5 h-5" style={{ color: SECONDARY_COLOR }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-zinc-500">Geração de Caixa Total</p>
                    <p className={`text-base sm:text-lg font-bold font-numbers leading-tight break-words ${(fluxo.resumo?.total_geracao_caixa || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {formatCurrency(fluxo.resumo?.total_geracao_caixa || 0)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 rounded-lg bg-zinc-800 shrink-0">
                    <Wallet className="w-5 h-5 text-zinc-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-zinc-500">Retirada Média</p>
                    <p className="text-base sm:text-lg font-bold text-zinc-300 font-numbers leading-tight break-words">
                      {formatCurrency(fluxo.resumo?.retirada_media_mensal || 0)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Gráfico Real do Ano */}
        {dadosGraficoReal.length > 0 && (
          <Card className="bg-zinc-900 border-zinc-800" data-testid="fluxo-grafico-real-card">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5" style={{ color: SECONDARY_COLOR }} />
                Fluxo de Caixa Real - Mês a Mês
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={dadosGraficoReal}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="mes" stroke="#888" fontSize={12} />
                    <YAxis stroke="#888" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                      labelStyle={{ color: '#fff' }}
                      formatter={(value) => [formatCurrency(value), '']}
                    />
                    <Legend />
                    <Bar dataKey="entradas" name="Entradas Reais" fill={PRIMARY_COLOR} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="saidas" name="Saídas Reais" fill={DANGER_COLOR} radius={[4, 4, 0, 0]} />
                    <Line type="monotone" dataKey="saldo" name="Saldo Acumulado" stroke="#fff" strokeWidth={2} dot={{ fill: '#fff', r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabela de Fluxo Completa */}
        {fluxo && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <DollarSign className="w-5 h-5" style={{ color: PRIMARY_COLOR }} />
                Fluxo de Caixa Mensal
              </CardTitle>
              
              {/* Modal Fechar Mês */}
              <Dialog open={fecharMesOpen} onOpenChange={setFecharMesOpen}>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                  <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2">
                      <Lock className="w-5 h-5 text-green-400" />
                      Fechar Mês - {mesSelecionado?.mes}/{mesSelecionado?.ano}
                    </DialogTitle>
                  </DialogHeader>
                  
                  {mesSelecionado && (
                    <div className="space-y-4">
                      <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <p className="text-sm text-blue-300">
                          Para fechar o mês, informe os saldos reais do banco. 
                          O sistema calculará o <strong>resíduo</strong> — se for diferente de ZERO, 
                          significa que há lançamentos faltando.
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-zinc-300">Saldo Inicial (R$)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={fechamentoForm.saldo_inicial}
                            onChange={(e) => setFechamentoForm({...fechamentoForm, saldo_inicial: e.target.value})}
                            className="bg-zinc-800 border-zinc-700 text-white"
                            placeholder="Início do mês"
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-300">Saldo Final (R$)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={fechamentoForm.saldo_final}
                            onChange={(e) => setFechamentoForm({...fechamentoForm, saldo_final: e.target.value})}
                            className="bg-zinc-800 border-zinc-700 text-white"
                            placeholder="Final do mês"
                          />
                        </div>
                      </div>
                      
                      {/* Preview do cálculo */}
                      {fechamentoForm.saldo_inicial && fechamentoForm.saldo_final && (() => {
                        const mesData = fluxo?.fluxo_mensal.find(m => m.mes === mesSelecionado.mes && m.ano === mesSelecionado.ano);
                        if (!mesData) return null;
                        
                        const saldoInicial = parseFloat(fechamentoForm.saldo_inicial) || 0;
                        const saldoFinal = parseFloat(fechamentoForm.saldo_final) || 0;
                        const entradas = mesData.entradas_caixa + mesData.outras_entradas_aportes;
                        const saidas = mesData.saidas_operacionais + mesData.investimentos_realizados + mesData.parcelas_financiamento + mesData.retirada_socio;
                        const calculado = saldoInicial + entradas - saidas;
                        const residuo = saldoFinal - calculado;
                        
                        return (
                          <div className={`p-4 rounded-lg border ${Math.abs(residuo) < 0.01 ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div className="text-zinc-400">Saldo Inicial:</div>
                              <div className="text-white text-right">{formatCurrency(saldoInicial)}</div>
                              
                              <div className="text-zinc-400">+ Entradas:</div>
                              <div className="text-green-400 text-right">{formatCurrency(entradas)}</div>
                              
                              <div className="text-zinc-400">- Saídas:</div>
                              <div className="text-red-400 text-right">{formatCurrency(saidas)}</div>
                              
                              <div className="text-zinc-400">= Calculado:</div>
                              <div className="text-white text-right font-bold">{formatCurrency(calculado)}</div>
                              
                              <div className="text-zinc-400">Saldo Real:</div>
                              <div className="text-white text-right">{formatCurrency(saldoFinal)}</div>
                              
                              <div className="col-span-2 border-t border-zinc-700 my-2"></div>
                              
                              <div className="text-zinc-400 font-bold">RESÍDUO:</div>
                              <div className={`text-right font-bold ${Math.abs(residuo) < 0.01 ? 'text-green-400' : 'text-red-400'}`}>
                                {formatCurrency(residuo)}
                                {Math.abs(residuo) < 0.01 ? ' ✓' : ' ✗'}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      
                      <div>
                        <Label className="text-zinc-300">Observação</Label>
                        <Input
                          value={fechamentoForm.observacao}
                          onChange={(e) => setFechamentoForm({...fechamentoForm, observacao: e.target.value})}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          placeholder="Ex: Conferido com extrato"
                        />
                      </div>
                      
                      <Button 
                        onClick={handleFecharMes} 
                        className="w-full" 
                        style={{ backgroundColor: PRIMARY_COLOR }}
                      >
                        <Lock className="w-4 h-4 mr-2" />
                        Fechar Mês
                      </Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    {/* Nível 1: grupos */}
                    <tr className="text-[10px] uppercase tracking-[0.12em] font-semibold">
                      <th className="py-2 px-2 sticky left-0 bg-zinc-900 z-10"></th>
                      <th colSpan={3} className="py-2 px-3 text-center text-indigo-300/90 bg-indigo-500/10 border-l border-zinc-700/50 rounded-t-md">Faturamento &amp; Recebíveis</th>
                      <th colSpan={2} className="py-2 px-3 text-center text-cyan-300/90 bg-cyan-500/10 border-l border-zinc-700/50 rounded-t-md">Recebido no Caixa</th>
                      <th colSpan={4} className="py-2 px-3 text-center text-amber-300/90 bg-amber-500/10 border-l border-zinc-700/50 rounded-t-md">Saídas &amp; Geração</th>
                      <th colSpan={2} className="py-2 px-3 text-center text-slate-300/90 bg-slate-500/10 border-l border-zinc-700/50 rounded-t-md">Saldos</th>
                      <th colSpan={2} className="py-2 px-3 text-center text-emerald-300/90 bg-emerald-500/15 border-l border-zinc-700/50 rounded-t-md">Resultado</th>
                      <th className="py-2 px-3 text-center text-violet-300/90 bg-violet-500/10 border-l border-zinc-700/50 rounded-t-md">Capital</th>
                      <th className="py-2 px-2 border-l border-zinc-700/50"></th>
                    </tr>
                    {/* Nível 2: colunas */}
                    <tr className="border-b-2 border-zinc-700 h-11 text-[11px] tracking-wide whitespace-nowrap align-bottom [&>th]:whitespace-nowrap [&>th]:align-bottom">
                      <th className="text-left py-2.5 px-2 font-semibold sticky left-0 bg-zinc-900 z-10 text-zinc-200">Mês</th>
                      <th className="text-center py-2.5 px-2 font-semibold text-indigo-200/90 bg-indigo-500/5 border-l border-zinc-700/50">Faturamento</th>
                      <th className="text-center py-2.5 px-2 font-semibold text-indigo-200/90 bg-indigo-500/5" title="Somatória do que deve cair neste mês conforme cadastrado na Gestão de Receita (à vista + parcelas que vencem agora, pelos perfis)">Estimativa</th>
                      <th className="text-center py-2.5 px-2 font-semibold text-indigo-200/90 bg-indigo-500/5" title="Caixa Futuro do mês = o que você FATUROU neste mês e vai receber em meses seguintes (vendas a prazo). Vem da Gestão de Receita, é exclusivo deste mês (não acumula). O acumulado geral fica na Carteira, na Saúde Financeira.">Caixa Futuro</th>
                      <th className="text-center py-2.5 px-2 font-semibold text-cyan-200/90 bg-cyan-500/5 border-l border-zinc-700/50" title="OFX classificado como recebimento operacional (receitas do banco)">Recebido Real</th>
                      <th className="text-center py-2.5 px-2 font-semibold text-cyan-200/90 bg-cyan-500/5" title="Prova final: Recebido Real − Estimativa. Positivou (entrou mais) ou negativou (entrou menos que o previsto na Gestão de Receita)">Dif. Bancária</th>
                      <th className="text-center py-2.5 px-2 font-semibold text-amber-200/90 bg-amber-500/5 border-l border-zinc-700/50" title="Saídas operacionais realizadas (OFX)">Saídas Op.</th>
                      <th className="text-center py-2.5 px-2 font-semibold text-amber-200/90 bg-amber-500/5" title="Geração de caixa operacional = Estimativa − Saídas operacionais">Geração</th>
                      <th className="text-center py-2.5 px-2 font-semibold text-amber-200/90 bg-amber-500/5" title="Investimento (maquinário, obras, bens) classificado via categoria no OFX">Investimento</th>
                      <th className="text-center py-2.5 px-2 font-semibold text-amber-200/90 bg-amber-500/5" title="Despesa Pontual (gasto único) classificada via categoria no OFX">Desp. Pontual</th>
                      <th className="text-center py-2.5 px-2 font-semibold text-slate-200/90 bg-slate-500/5 border-l border-zinc-700/50">Cx Inicial</th>
                      <th className="text-center py-2.5 px-2 font-semibold text-slate-200/90 bg-slate-500/5">Cx Final</th>
                      <th className="text-center py-2.5 px-2 font-semibold border-l border-zinc-700/50 bg-emerald-500/10 text-emerald-300" title="Lucro Líquido do mês = Estimativa − (Saídas OP + Desp. Pontual + Investimento)">Lucro Líquido</th>
                      <th className="text-center py-2.5 px-2 font-semibold bg-emerald-500/10 text-emerald-300" title="Margem de Lucro = Lucro Líquido ÷ Estimativa × 100">Margem</th>
                      <th className="text-center py-2.5 px-2 font-semibold text-violet-200/90 bg-violet-500/5 border-l border-zinc-700/50" title="Aportes de capital / empréstimos — separados do operacional">Aportes</th>
                      <th className="text-center py-2.5 px-2 font-semibold text-zinc-300 border-l border-zinc-700/50">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fluxoFiltrado.map((mes, index) => (
                      <tr 
                        key={index} 
                        className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 h-12 ${
                          mes.is_atual ? 'bg-zinc-800/50' : ''
                        } ${mes.is_futuro ? 'opacity-70' : ''}`}
                      >
                        {/* Mês */}
                        <td className="py-2 px-2 sticky left-0 bg-zinc-900 align-middle">
                          <div className="flex items-center gap-2 h-full">
                            <span className="text-white capitalize font-medium">
                              {mes.mes?.substring(0, 3)}/{mes.ano?.toString().substring(2)}
                            </span>
                            {mes.is_atual && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">Atual</span>
                            )}
                            {mes.saldo_final_real !== null && !mes.is_futuro && (
                              <Lock className="w-3 h-3 text-green-400" title="Mês fechado" />
                            )}
                          </div>
                        </td>
                        
                        {/* Faturamento Total */}
                        <td className="py-2 px-2 text-right font-numbers align-middle whitespace-nowrap text-white align-middle">
                          <button onClick={() => openDrill('faturamento', mes.mes, mes.ano)} className="hover:underline decoration-dotted underline-offset-2 cursor-pointer" title="Ver composição" data-testid={`drill-faturamento-${mes.mes}-${mes.ano}`}>
                            {formatCurrency(mes.faturamento_total)}
                          </button>
                        </td>
                        
                        {/* Entradas de Caixa */}
                        <td className="py-2 px-2 text-right font-numbers align-middle whitespace-nowrap" style={{ color: PRIMARY_COLOR }}>
                          <button onClick={() => openDrill('entrada_prevista', mes.mes, mes.ano)} className="hover:underline decoration-dotted underline-offset-2 cursor-pointer" title="Ver o que compõe a estimativa" data-testid={`drill-entradas-${mes.mes}-${mes.ano}`}>
                            {formatCurrency(mes.entradas_caixa)}
                          </button>
                        </td>
                        
                        {/* Caixa Futuro = parte do faturado DO MÊS que cai em meses seguintes (Gestão de Receita) */}
                        <td className={`py-2 px-2 text-right font-numbers align-middle whitespace-nowrap ${getCelulaClass(mes.gap_fat_caixa || 0, 'gap')}`}>
                          {(mes.gap_fat_caixa || 0) > 0 ? (
                            <button
                              className="hover:underline decoration-dotted underline-offset-2 cursor-pointer inline-flex items-center gap-1"
                              onClick={() => openDrill('caixa_futuro', mes.mes, mes.ano)}
                              data-testid={`caixa-futuro-${mes.mes}-${mes.ano}`}
                              title="Caixa Futuro = o que você faturou neste mês e vai receber depois (vendas a prazo). Clique para ver as vendas."
                            >
                              {formatCurrency(mes.gap_fat_caixa || 0)}
                            </button>
                          ) : (
                            formatCurrency(mes.gap_fat_caixa || 0)
                          )}
                        </td>
                        
                        {/* Recebido Real (OFX) */}
                        <td className="py-2 px-2 text-right font-numbers align-middle whitespace-nowrap text-cyan-400/70 border-l border-zinc-700/50" data-testid={`receb-real-${mes.mes}-${mes.ano}`}>
                          {mes.recebido_real > 0 ? (
                            <button onClick={() => openDrill('recebido_real', mes.mes, mes.ano)} className="hover:underline decoration-dotted underline-offset-2 cursor-pointer" title="Ver lançamentos do banco">
                              {formatCurrency(mes.recebido_real)}
                            </button>
                          ) : <span className="text-zinc-600">—</span>}
                        </td>
                        
                        {/* Diferença Bancária (PROVA FINAL: Recebido Real OFX − Entrada previsto) */}
                        <td className={`py-2 px-2 text-right font-numbers align-middle whitespace-nowrap ${
                          mes.recebido_real === 0 ? 'text-zinc-600'
                          : mes.divergencia_previsto_real > 0 ? 'text-green-400'
                          : mes.divergencia_previsto_real < 0 ? 'text-red-400'
                          : 'text-zinc-400'
                        }`} data-testid={`div-${mes.mes}-${mes.ano}`}
                          title={mes.recebido_real === 0 ? 'Sem OFX importado' : mes.divergencia_previsto_real > 0 ? 'POSITIVOU: entrou MAIS que o previsto na Gestão de Receita (antecipação de recebíveis ou perfil de venda subestimado)' : mes.divergencia_previsto_real < 0 ? 'NEGATIVOU: entrou MENOS que o previsto (taxa de cartão, atraso ou inadimplência)' : 'Bateu certinho com o cadastro'}>
                          {mes.recebido_real === 0 ? '—' : (
                            <button onClick={() => openDrill('diferenca_bancaria', mes.mes, mes.ano)} className="hover:underline decoration-dotted underline-offset-2 cursor-pointer">
                              {formatCurrency(mes.divergencia_previsto_real)}
                            </button>
                          )}
                        </td>
                        
                        {/* Saídas Operacionais */}
                        <td className="py-2 px-2 text-right font-numbers align-middle whitespace-nowrap" style={{ color: DANGER_COLOR }}>
                          <button onClick={() => openDrill('despesa_operacional', mes.mes, mes.ano)} className="hover:underline decoration-dotted underline-offset-2 cursor-pointer" title="Ver custos operacionais" data-testid={`drill-despesa-${mes.mes}-${mes.ano}`}>
                            {formatCurrency(mes.saidas_operacionais)}
                          </button>
                        </td>
                        
                        {/* Geração de Caixa */}
                        <td className={`py-2 px-2 text-right font-numbers align-middle whitespace-nowrap font-bold bg-zinc-800/50 ${getCelulaClass(mes.geracao_caixa, 'positivo')}`}>
                          <button onClick={() => openDrill('geracao', mes.mes, mes.ano)} className="hover:underline decoration-dotted underline-offset-2 cursor-pointer" title="Ver composição (Estimativa − Saídas Op.)" data-testid={`drill-geracao-${mes.mes}-${mes.ano}`}>
                            {formatCurrency(mes.geracao_caixa)}
                          </button>
                        </td>
                        
                        {/* Investimentos */}
                        <td className="py-2 px-2 text-right font-numbers align-middle whitespace-nowrap text-orange-400">
                          {mes.investimentos_realizados > 0 ? (
                            <button onClick={() => openDrill('investimento', mes.mes, mes.ano)} className="hover:underline decoration-dotted underline-offset-2 cursor-pointer" title="Ver investimentos" data-testid={`drill-investimento-${mes.mes}-${mes.ano}`}>
                              {formatCurrency(mes.investimentos_realizados)}
                            </button>
                          ) : '-'}
                        </td>
                        
                        {/* Despesa Pontual */}
                        <td className="py-2 px-2 text-right font-numbers align-middle whitespace-nowrap text-purple-400">
                          {mes.parcelas_financiamento > 0 ? (
                            <button onClick={() => openDrill('despesa_pontual', mes.mes, mes.ano)} className="hover:underline decoration-dotted underline-offset-2 cursor-pointer" title="Ver despesas pontuais">
                              {formatCurrency(mes.parcelas_financiamento)}
                            </button>
                          ) : '-'}
                        </td>
                        
                        {/* Caixa Inicial */}
                        <td className="py-2 px-2 text-right font-numbers align-middle whitespace-nowrap text-zinc-400">
                          <button onClick={() => openDrill('caixa_final', mes.mes, mes.ano)} className="hover:underline decoration-dotted underline-offset-2 cursor-pointer" title="Saldo de abertura = Caixa Final do mês anterior (ou saldo informado). Clique para ver a composição do mês" data-testid={`drill-cxinicial-${mes.mes}-${mes.ano}`}>
                            {formatCurrency(mes.caixa_inicial)}
                          </button>
                        </td>
                        
                        {/* Caixa Final */}
                        <td className={`py-2 px-2 text-right font-numbers align-middle whitespace-nowrap font-medium ${getCelulaClass(mes.caixa_final_calculado, 'positivo')}`}>
                          <button onClick={() => openDrill('caixa_final', mes.mes, mes.ano)} className="hover:underline decoration-dotted underline-offset-2 cursor-pointer" title="Caixa Final = Cx Inicial + Estimativa − Saídas OP − Pontual − Investimento + Aportes. Clique para ver a composição" data-testid={`drill-cxfinal-${mes.mes}-${mes.ano}`}>
                            {formatCurrency(mes.caixa_final_calculado)}
                          </button>
                        </td>
                        
                        {/* Lucro Líquido (leitura: Estimativa − Saídas OP − Pontual − Investimento) */}
                        <td className={`py-2 px-2 text-right font-numbers align-middle whitespace-nowrap font-bold bg-green-500/10 ${getCelulaClass(mes.lucro_liquido, 'positivo')}`}>
                          <button
                            onClick={() => openDrill('lucro_liquido', mes.mes, mes.ano)}
                            className="hover:underline decoration-dotted underline-offset-2 cursor-pointer"
                            title={`Lucro Líquido = Estimativa ${formatCurrency(mes.entradas_caixa)} − Saídas OP ${formatCurrency(mes.saidas_operacionais)} − Pontual ${formatCurrency(mes.parcelas_financiamento)} − Investimento ${formatCurrency(mes.investimentos_realizados)}`}
                            data-testid={`fluxo-lucro-${mes.mes}-${mes.ano}`}
                          >
                            {formatCurrency(mes.lucro_liquido)}
                          </button>
                        </td>
                        
                        {/* Margem de Lucro % (leitura) */}
                        <td className={`py-2 px-2 text-right font-numbers align-middle whitespace-nowrap font-bold bg-green-500/10 ${getCelulaClass(mes.lucro_liquido, 'positivo')}`}>
                          <button
                            onClick={() => openDrill('lucro_liquido', mes.mes, mes.ano)}
                            className="hover:underline decoration-dotted underline-offset-2 cursor-pointer"
                            title={`Margem = Lucro Líquido ${formatCurrency(mes.lucro_liquido)} ÷ Estimativa ${formatCurrency(mes.entradas_caixa)} × 100. Clique para ver a composição do Lucro`}
                            data-testid={`fluxo-margem-${mes.mes}-${mes.ano}`}
                          >
                            {mes.margem_lucro_pct !== null && mes.margem_lucro_pct !== undefined ? `${mes.margem_lucro_pct}%` : '—'}
                          </button>
                        </td>
                        
                        {/* Aportes */}
                        <td className={`py-2 px-2 text-right font-numbers align-middle whitespace-nowrap ${mes.outras_entradas_aportes >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {mes.outras_entradas_aportes !== 0 ? (
                            <button onClick={() => openDrill('aportes', mes.mes, mes.ano)} className="hover:underline decoration-dotted underline-offset-2 cursor-pointer" title="Ver aportes/empréstimos">
                              {formatCurrency(mes.outras_entradas_aportes)}
                            </button>
                          ) : '-'}
                        </td>
                        
                        {/* Ação - Editar/Fechar Mês (editar saldo inicial e final) */}
                        <td className="py-2 px-2 text-center align-middle">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setMesSelecionado({ mes: mes.mes, ano: mes.ano });
                                setFecharMesOpen(true);
                              }}
                              className="text-zinc-400 hover:text-green-400 h-7 px-2"
                              title="Editar saldo inicial/final do mês"
                              data-testid={`fluxo-editar-saldo-${mes.mes}-${mes.ano}`}
                            >
                              {mes.saldo_final_real !== null ? <CheckCircle2 className="w-4 h-4 text-green-400" /> : <Lock className="w-3 h-3" />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Legenda */}
              <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-zinc-800 text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded" style={{ backgroundColor: PRIMARY_COLOR }}></span>
                  <span className="text-zinc-400">Estimativa</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded" style={{ backgroundColor: DANGER_COLOR }}></span>
                  <span className="text-zinc-400">Saídas OP</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded bg-yellow-400"></span>
                  <span className="text-zinc-400">A Receber (Gap do mês)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Lock className="w-3 h-3 text-green-400" />
                  <span className="text-zinc-400">Mês fechado</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <DrillDownModal
          open={drillDown.open}
          onOpenChange={(v) => setDrillDown((d) => ({ ...d, open: v }))}
          gymId={selectedGym?.id}
          tipo={drillDown.tipo}
          mes={drillDown.mes}
          ano={drillDown.ano}
        />
      </div>
    </DashboardLayout>
  );
};

export default FluxoCaixaPage;
